interface Window {
    finnUserId?: string;
    document: Document;
}

export type ValidPayload = Device | Actor | Experiments | Provider | PulseObject;

interface Tracker {
    update(payload: ValidPayload, merge: boolean): void;
}

interface PulseObject {
    object: {
        url: string;
    };
}

interface Device {
    device: {
        additionalDeviceIds: {
            '@id': string;
            component?: string;
        }[];
    };
}

interface Actor {
    actor: {
        additionalIds: {
            '@id': string;
            component: string;
        }[];
    };
}

interface Experiment {
    platform: string;
    id: string;
    variant: string;
}

interface Experiments {
    experiments: Experiment[];
}

interface Provider {
    provider: {
        product: string;
    };
}

export class PluginConfig {
    constructor(readonly window: Window) {}
}

const DATA_APP_NAME = 'data-application-name';

export default class FinnPulsePlugin {
    private readonly tracker: Tracker;
    private win: Window;

    constructor(tracker: Tracker, config: PluginConfig) {
        this.tracker = tracker;

        if (config.window) {
            this.win = config.window;
        } else {
            this.win = window;
        }
    }

    overrideIdURLs(): void {
        const url = this.win.document.URL;
        this.overrideFinnboxURL(url);
        this.overrideInnfinnURL(url);
        this.overrideRedirectAppURL(url);
    }

    overrideFinnboxURL(url: string): void {
        if (this.isFinnboxURL(url)) {
            const cleanURL = this.cleanFinnboxURL(url);
            this.overrideURL(cleanURL);
        }
    }

    overrideInnfinnURL(url: string): void {
        if (this.isInnfinnURL(url)) {
            const cleanURL = this.cleanInnfinnURL(url);
            this.overrideURL(cleanURL);
        }
    }

    overrideRedirectAppURL(url: string): void {
        if (this.isRedirectAppURL(url)) {
            const cleanURL = this.cleanRedirectAppURL(url);
            this.overrideURL(cleanURL);
        }
    }

    injectFinnData(): void {
        this.injectFinnIds();
        this.injectAbTestData();
        this.injectApplicationName();
    }

    injectFinnIds(): void {
        this.injectDeviceId();
        this.injectLoginId();
    }

    injectDeviceId(): void {
        const deviceId = this.getCookie('USERID');
        if (deviceId && deviceId !== '') {
            const device: Device = {
                device: {
                    additionalDeviceIds: [
                        {
                            '@id': `sdrn:finn:device:id:${deviceId}`,
                            component: 'finn',
                        },
                    ],
                },
            };
            this.tracker.update(device, true);
        }
    }

    injectLoginId(): void {
        const finnUserId = this.win.finnUserId;
        if (finnUserId && finnUserId !== '') {
            const actor: Actor = {
                actor: {
                    additionalIds: [
                        {
                            '@id': `sdrn:finn:user:${finnUserId}`,
                            component: 'finn',
                        },
                    ],
                },
            };
            this.tracker.update(actor, true);
        }
    }

    injectAbTestData(): void {
        const abTestData = this.getCookie('abtest');

        if (!abTestData || abTestData === '' || abTestData.charAt(0) !== '{') {
            return;
        }

        let abTestJson;
        try {
            abTestJson = JSON.parse(abTestData);
        } catch (e) {
            return;
        }

        const experiments: Experiment[] = [];

        Object.keys(abTestJson).forEach((key) => {
            if (!key) {
                return;
            }

            // We currently can't reliably deduce the variant from
            // the value found in the 'abtest' cookie, since '_'
            // also sometimes is used in toggle names. Therefore (for now)
            // set variant to an empty string (it's required in the schema)
            // and use the complete value in the 'id'.
            experiments.push({
                platform: 'finn.no',
                id: key,
                variant: '',
            });
        });

        if (experiments.length > 0) {
            this.tracker.update({ experiments: experiments }, true);
        }
    }

    injectApplicationName(): void {
        const applicationName = this.getAttributeValue(DATA_APP_NAME);
        if (applicationName) {
            const provider: Provider = {
                provider: {
                    product: applicationName,
                },
            };
            this.tracker.update(provider, true);
        }
    }

    private overrideURL(url: string): void {
        const object: PulseObject = {
            object: {
                url: url,
            },
        };
        this.tracker.update(object, true);
    }

    private isFinnboxURL(url: string): boolean {
        return url.includes('/meldinger');
    }

    private cleanFinnboxURL(url: string): string {
        url = url.replace(/(\/meldinger\/ny)\/[0-9]+$/, '$1/adId');
        url = url.replace(/(\/meldinger\/id)\/.*$/, '$1/ID');
        return url;
    }

    private isInnfinnURL(url: string): boolean {
        return url.includes('/innfinn');
    }

    private cleanInnfinnURL(url: string): string {
        return url.replace(/(\/innfinn\/adselection\/ad)\/[0-9]+/, '$1/adId');
    }

    private isRedirectAppURL(url: string): boolean {
        const re = /www\.finn\.no\/[0-9]+$/;
        return re.test(url);
    }

    private cleanRedirectAppURL(url: string): string {
        return url.replace(/(www\.finn\.no)\/[0-9]+$/, '$1/adId');
    }

    private getCookie(key: string): string | null {
        if (!key || !this.win.document.cookie) {
            return null;
        }
        return (
            decodeURIComponent(
                this.win.document.cookie.replace(
                    new RegExp(
                        `(?:(?:^|.*;)\\s*${encodeURIComponent(key).replace(
                            /[-.+*]/g,
                            '\\$&',
                        )}\\s*\\=\\s*([^;]*).*$)|^.*$`,
                    ),
                    '$1',
                ),
            ) || null
        );
    }

    private getAttributeValue(attributeName: string): string | null {
        try {
            let attributeValue;
            if (this.win.document.currentScript) {
                attributeValue = this.win.document.currentScript.getAttribute(attributeName);
            }
            if (!attributeValue) {
                const el = this.win.document.querySelector(`[${attributeName}]`);
                if (el) {
                    attributeValue = el.getAttribute(attributeName);
                }
            }
            return attributeValue ?? null;
        } catch (e) {
            return null;
        }
    }
}
